// eslint is half-broken (shocker) when detecting unused stuff,
// but I don't want to disable the rule for the whole file.
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { saveAs } from 'file-saver';
import Vue from 'vue';
import DateOptions from '@/models/DateOptions';
// eslint-disable-next-line no-unused-vars
import Camera from '@/models/DOMCamera';
import { PacketType } from '@/models/Packet';
// eslint-disable-next-line no-unused-vars
import ProjectServer from '@/models/ProjectServer';
// eslint-disable-next-line no-unused-vars
import StringPacking from '@/models/StringPacking';
// eslint-disable-next-line no-unused-vars
import Tag from '@/models/Tag';
// eslint-disable-next-line no-unused-vars
import Timeline from '@/models/Timeline';
import Vector2 from '@/models/Vector2';
import { SendPacket } from '@/networking/SendPacket';
import Mutations from '@/store/mutations';
import store from '@/store/store';
import ColorUtility from '@/util/ColorUtility';
import StringUtil from '@/util/StringUtil';
import { CalculateNodeTextSize } from '@/util/TextSize';
import NodeRequests from '@/networking/Node';

export enum NodeType
{
    // eslint-disable-next-line no-unused-vars
    TimelineEvent = 0,
    // eslint-disable-next-line no-unused-vars
    TimelineEra = 1,
    // eslint-disable-next-line no-unused-vars
    MapNode = 2,
}

const y2ms =  (365 * 24 * 60 * 60 * 1000);
export default class Node
{
    static readonly NodeType = NodeType; // allows Node.NodeType.*

    nodeid = -1;
    type = NodeType.TimelineEvent;
    name = '';
    important = false;
    description = '';
    references = '';
    image = '';
    video = '';
    imageCaption = '';
    audio = '';
    data = {};
    dateStart: Date = new Date(0);
    dateEnd: Date = new Date(0);
    dateOptions: DateOptions = new DateOptions(0);
    tags: Tag[] = [];
    position: Vector2 = new Vector2(0, 0);
    relatedArray : Node[] = [];
    trackID = 0;
    size = 1;

    component: any = null;
    /** 
     * Array of Timelines of which the Node is a member.
     * Maintained only by the frontend via the addTimelineNode and removeTimelineNode mutations
     * and full sync packets.
     * Used for logic involving nodes that are linked across timelines.
     * */
    timelines: Timeline[] = [];

    public constructor(nodeid : number = -1, dateStart : Date = new Date(), color : any = null)
    {
        this.nodeid = nodeid;
        this.dateStart = dateStart;
        if (color != null)
        {
            this.color.r = color.r;
            this.color.g = color.g;
            this.color.b = color.b;
        }
        else
        {
            this.computeEraColor();
        }
    }

    public static readonly TypeOffset : number = 140;
    public static charToType(typeChar : string) : NodeType 
    {
        return <NodeType>(typeChar.charCodeAt(0) - this.TypeOffset);
    }

    public static typeToChar(type : NodeType) : string
    {
        return String.fromCharCode(type + this.TypeOffset);
    }

    public getTypeChar() : string
    {
        return Node.typeToChar(this.type);
    }

    public isEra() : boolean
    {
        return this.type == NodeType.TimelineEra;
    }

    public isEvent() : boolean
    {
        return this.type == NodeType.TimelineEvent;
    }
    
    private _oldTitle : string = ''
    private _oldTextSize : Vector2 = new Vector2(0,0);
    private _oldRadius : number = -1;
    private _oldMult : number = 1;
    public getRawTextSize() : Vector2
    {
        // Dependent on radius size bc it affects the font size
        if (this._oldTitle != this.name || this._oldRadius != (window as any).CircleRadius)
        {
            this._oldRadius = (window as any).CircleRadius;
            this._oldTitle = this.name;
            this._oldTextSize = CalculateNodeTextSize(this.name);
        }
        return this._oldTextSize;
    }

    public getMaxTextDistanceFromOrigin(cam : Camera)
    {
        let mult : number;
        if (!cam)
        {
            mult = this._oldMult;
        }
        else
        {
            mult = y2ms / (cam.yearToPixels as number);
            this._oldMult = mult;
        }
        
        const rawSize = this.getRawTextSize();
        
        // Adding radius
        return (rawSize.x + this._oldRadius) * mult;
    }

    public getBounds(cam : Camera) : any
    {
        const textSize = this.getMaxTextDistanceFromOrigin(cam);
        // eslint-disable-next-line no-unused-vars
        const mult = this._oldMult;
        
        // TODO!: We should be able to get rid of this once we
        // have text-based intersections working properly.  Otherwise
        // new nodes will fail to fall into the layout correctly
        // until a zoom.  Plus, relying on the DOM directly is really sketchy.
        if (this.component)
        {
            return this.component.bound;
        }

        let circleRad = 0;
        if ((window as any).CircleRadius)
        {
            circleRad = (window as any).CircleRadius;
        }

        // 4 is the divisor from TimelineViewerNode->circleScale
        const circleScale = 1 + this.numRelated() / 4;
        
        // This accounts for the left half of our circle, including scaling
        // from connections.  Divides by two bc we're only looking for the
        // left half (despite being called radius, it's actually diameter - oops).
        // Then multiplies by the scaling factor.
        const leftHalfOffset = (circleRad / 2) * circleScale * this._oldMult;
        return {
            min: {
                x: Math.floor(this.dateStart.getTime() - leftHalfOffset)
            },
            max: {
                x: Math.ceil(Math.max(this.dateEnd.getTime(), this.dateStart.getTime() + textSize))
            }
        };
    }

    /**
     * Should always be used except when creating a brand new node.
     * Handles re-sorting related nodes structures (which get used by the layout algorithm)
     */
    public updateDateStart(newDate : Date)
    {
        if (this.dateStart !== newDate) // only do something if there was an actual change
        {
            Vue.set(this, 'dateStart', newDate);
            this.forAllRelated((n : Node) =>
            {
                n.reSortRelated(this);
            });
        }
    }

    public forAllRelated(f : (n : Node) => any)
    {
        for(const node of this.relatedArray)
        {
            f(node);
        }
    }

    public isParentless() : Boolean
    {
        return this.relatedArray.length == 0 || this.relatedArray.find((n)=>n.dateStart < this.dateStart) == null;
    }

    /** is node parentless if only considering nodes in the specified Set? */
    public isParentlessFiltered(nodesSet : Set<Node>) : Boolean
    {
        return !(this.relatedArray.some((n)=>nodesSet.has(n) && (n.dateStart < this.dateStart)));
    }

    public isChildless() : Boolean
    {
        return this.relatedArray.length == 0 || this.relatedArray.find((n)=>n.dateStart > this.dateStart) == null;
    }

    /** is node childless if only considering nodes in the specified Set? */
    public isChildlessFiltered(nodesSet : Set<Node>) : Boolean
    {
        return !(this.relatedArray.some((n)=>nodesSet.has(n) && (n.dateStart > this.dateStart)));
    }

    /**
     * Iterates in reverse dateStart order.
     */
    public forAllChildren(f : (n : Node) => void)
    {
        for(let i = this.relatedArray.length - 1; i >= 0; --i)
        {
            const node = this.relatedArray[i];
            if (node.dateStart <= this.dateStart) return;
            f(node);
        }
    }

    public numRelated() : number
    {
        return this.relatedArray.length;
    }

    public hasRelated(node : Node) : Boolean
    {
        return this.relatedArray.includes(node);
    }

    public addRelated(node : Node)
    {
        if (this.relatedArray.includes(node))
            return;
        
        this.relatedArray.push(node);
        this.relatedArray.sort((a,b)=>
        {
            return a.dateStart.getTime() - b.dateStart.getTime();
        });
    }

    public removeRelated(node : Node)
    {
        const ind = this.relatedArray.indexOf(node);
        if (ind < 0)
        {
            console.log(`Related array for ${this.name} doesn't contain node ${node.name}`);
            return;
        }
        console.log(`Removing connection from ${this.name}->${node.name}`);
        this.relatedArray.splice(ind, 1);
    }

    /**
     * For date changes. 
     * Can definitely be optimized if necessary.
     * Starting with simplest implementation.
     */
    public reSortRelated(node : Node)
    {
        this.removeRelated(node);
        this.addRelated(node);
    }

    public addToDocument(doc : Document)
    {
        console.log(`adding ${this.name}`);

        if (this.isEra())
        {
            doc.addSection({
                children: [
                    new Paragraph({
                        heading: HeadingLevel.HEADING_2,
                        children: [
                            new TextRun({
                                text: 'Era: ' + this.name + ' from ' + this.dateStart.getUTCBCString(this.dateOptions) + ' to ' + this.dateEnd.getUTCBCString(this.dateOptions),
                                size: 26,
                                bold: true, 
                                color: 'black'
                            }),
                        ]
                    })
                ]
            });

            const p = new Paragraph('');

            p.addRun(new TextRun({ text: this.description }));
            p.addRun(new TextRun('').break());

            doc.addSection({
                children: [p]
            });
        }
        else
        {
            let p = new Paragraph(
                {
                    heading: HeadingLevel.HEADING_3,
                    spacing:
                {
                    after: 0
                }
                });

            p.addRun(new TextRun({ text: 'Name', bold: true, color: 'black' }));
            p.addRun(new TextRun({ text: ' of the event: ' + this.name, color: 'black' }));

            doc.addSection({
                children: [p]
            });

            p = new Paragraph('');

            if (this.dateEnd.valueOf() != this.dateStart.valueOf())
            {
                p.addRun(new TextRun({ text: 'Date', bold: true }));
                p.addRun(new TextRun(' the event started: ' + this.dateStart.getUTCBCString(this.dateOptions)));
                p.addRun(new TextRun({ text: 'Date', bold: true }).break());
                p.addRun(new TextRun(' the event ended: ' + this.dateEnd.getUTCBCString(this.dateOptions)));
            }
            else
            {
                p.addRun(new TextRun({ text: 'Date', bold: true }));
                p.addRun(new TextRun(' of the event: ' + this.dateStart.getUTCBCString(this.dateOptions)));
            }

            if (this.image != null && this.image.trim() !== '')
            {
                p.addRun(new TextRun({ text: 'Image URL', bold: true }).break());
                p.addRun(new TextRun(': ' + this.image.trim()));
            }

            if (this.video != null && this.video.trim() !== '')
            {
                p.addRun(new TextRun({ text: 'Video URL', bold: true }).break());
                p.addRun(new TextRun(': ' + this.video.trim()));
            }

            if (this.audio != null && this.audio.trim() !== '')
            {
                p.addRun(new TextRun({ text: 'Audio URL', bold: true }).break());
                p.addRun(new TextRun(': ' + this.audio.trim()));
            }

            if (this.description !== '')
            {
                p.addRun(new TextRun({ text: 'Description', bold: true }).break());
                p.addRun(new TextRun(': ' + this.description));
                p.addRun(new TextRun('').break());
            }

            if (this.references !== '')
            {
                p.addRun(new TextRun({ text: 'Source', bold: true }).break());
                p.addRun(new TextRun(': ' + this.references));
                p.addRun(new TextRun('').break());
            }

            if (this.numRelated() !== 0)
            {
                p.addRun(new TextRun({ text: 'Connections', bold: true }).break());
                let number = 0;
                this.forAllRelated((related) =>
                {
                    p.addRun(new TextRun(++number + ') ').break());
                    if (related.dateStart.valueOf() > this.dateStart.valueOf())
                    {
                        // related is an effect
                        p.addRun(new TextRun(this.name + ' to ' + related.name));
                    }
                    else
                    {
                        // related is a cause
                        p.addRun(new TextRun(related.name + ' to ' + this.name));
                    }
                    const connection = store.getters.getConnection(related.nodeid, this.nodeid);
                    if (connection != null && connection.description !== '')
                    {
                        p.addRun(new TextRun(', \"' + connection.description + '\"'));
                    }
                    if (connection != null && connection.references !== '')
                    {
                        p.addRun(new TextRun(' Source: ' + connection.references));
                    }
                });
                p.addRun(new TextRun('').break());
            }

            doc.addSection({
                children: [p]
            });
        }
    }

    /** 
     * Exports this event to a docx file.
     */
    public exportToDocx(fileName? : string)
    {
        let exportName = 'node.docx';
        if (fileName == null || fileName === '')
        {
            // TODO: should probably be handled with regex
            const sanitizedName = 
                this.name
                    .replace('/', '_')
                    .replace('.', '_')
                    .replace('*', '_')
                    .replace('[', '_')
                    .replace(':', '_')
                    .replace('|', '_')
                    .replace(',', '_')
                    .replace('=', '_')
                    .replace(']', '_')
                    .replace(';', '_')
                    .replace('\\', '_');

            exportName = sanitizedName + '.docx';
        }
        else
        {
            exportName = fileName;
        }

        const doc = new Document();
        this.addToDocument(doc);

        console.log('Downloading: ' + exportName);

        Packer.toBlob(doc).then((blob) => 
        {
            saveAs(blob, exportName);
        });
    }
    
    get audio_url()
    {
        let processed = this.audio || '';
        if (processed.match(/http(s?):\/\/((www\.)?)dropbox.com\/s\/([^ ]*)\/([^ ]*)((\?dl=[01])?)/))
        {
            console.log('DROPBOX URL - POST PROCESSING');
            if (processed.startsWith("http://"))
            {
                processed.replace("http://", "https://");
            }
            if (processed.includes("dl="))
            {
                processed.replace("dl=0", "dl=1");
            }
            else
            {
                if (processed.includes("?"))
                {
                    processed = processed + "&dl=1";
                }
                else
                {
                    processed = processed + "?dl=1";
                }
            }
        }
        return processed;
    }

    get video_url()
    {
        let processed = this.video || '';
        if (processed.match(/http(s?):\/\/((www\.)?)dropbox.com\/s\/([^ ]*)\/([^ ]*)((\?dl=[01])?)/))
        {
            console.log('DROPBOX URL - POST PROCESSING');
            if (processed.startsWith("http://"))
            {
                processed.replace("http://", "https://");
            }
            if (processed.includes("dl="))
            {
                processed.replace("dl=0", "dl=1");
            }
            else
            {
                if (processed.includes("?"))
                {
                    processed = processed + "&dl=1";
                }
                else
                {
                    processed = processed + "?dl=1";
                }
            }
        }

        // support youtu.be links...
        if (processed.match(/http(s?):\/\/((www\.)?)youtu\.be\/([^?]+)/g))
        {
            processed = processed.replace('youtu.be/','youtube.com/watch?v=');
        }
        
        if (processed.match(/http(s?):\/\/((www\.)?)youtube\.com\/watch\?v=([^?]+)/g))
        {
            processed = processed.replace('watch?v=', 'embed/');
            if (processed.includes('&'))
            {
                processed = processed.substring(0, processed.indexOf('&'));
            }
        }
        return processed;
    }

    get is_video_url_valid()
    {
        const processed = this.video_url;
        if (!processed) return false;
        if (processed.trim() === '') return false;

        if (processed.endsWith('.mp4')) return true;
        if (processed.match(/http(s?):\/\/((www\.)?)youtube.com\/embed\/([^?]+)/g)) return true;
        if (processed.match(/http(s?):\/\/((www\.)?)dropbox.com\/s\/([^ ]*)\/([^ ]*)((\?dl=1)?)/)) return true;

        return false;
    }

    video_html(style = '')
    {
        if (!this.is_video_url_valid) return '';
        
        const processed = this.video_url;
        if (processed.includes('youtube.com'))
        {
            return `<iframe style="${style}" class="node-video" src="${processed}" title="YouTube Video Player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen>Failed to display YouTube video.  Please ensure that your browser is up to date and the video link is correct.</iframe>`;
        }
        return `<video controls class="node-video" src="${processed}"/>`;
    }
    
    color = { r: 0, g: 0, b: 0 };
    get eraBackgroundColorString()
    {
        if (!this.color)
        {
            this.color = {r: 0, g: 0, b: 0};
            this.computeEraColor();
        }
        return `rgba(${this.color.r}, ${this.color.g}, ${this.color.b}, 0.4)`;
    }

    public computeEraColor()
    {
        const yearFactor = (Math.abs(this.dateStart.getUTCFullYear()) % 50) / 100; // 0 to 0.5 - for consecutively made eras
        const idFactor = (this.nodeid % 10) / 20; // 0 to 0.5 - for side-by-side eras that match the year repeat
        const col = ColorUtility.HSVtoRGB(yearFactor + idFactor, 0.5, 0.5);
        this.color.r = col.r;
        this.color.g = col.g;
        this.color.b = col.b;
    }

    public addToCSV(csv : string) : string
    {
        const escName = StringUtil.csvEscapeString(this.name);
        const typeString = this.isEvent() ? '"event"' : '"era"';
        const importantString = !(this.isEvent()) ? '""' : (this.important ? '"Y"' : '"N"');
        const escDescription = StringUtil.csvEscapeString(this.description);
        const startDateString = this.dateStart.getUTCBCString(this.dateOptions);
        const endDateString = this.dateEnd.getUTCBCString(this.dateOptions);
        const escImage = StringUtil.csvEscapeString(this.image);
        const escImageCaption = StringUtil.csvEscapeString(this.imageCaption);
        const escAudio = StringUtil.csvEscapeString(this.audio);
        const escReferences = StringUtil.csvEscapeString(this.references);
        let escTag = '""';
        if (this.tags.length > 0 && this.tags[0] != null)
        {
            escTag = StringUtil.csvEscapeString(this.tags[0].name);
        }
        csv += '\n';
        csv += `${escName},${typeString},${escDescription},${escReferences},${importantString},${startDateString},${endDateString},${escImage},${escImageCaption},${escAudio},${escTag}`;
        return csv;
    }

    public static async createAndWait(node : Node, timelineid : any)
    {
        const created: Node = await NodeRequests.CreateNodeRaw(node, timelineid);
        if (created && created.nodeid && node.isEra() && node.color)
        {
            store.commit(Mutations.updateNodeColor, { nodeid: created.nodeid, color: node.color });
            SendPacket(PacketType.NodeUpdateColor, { nodeid: created.nodeid, color: node.color });
        }
        return created;
    }

    public static create(node : Node, timelineid : any, selectAfterCreation : boolean = true)
    {
        const req = this.createAndWait(node, timelineid);
        req.then(created=>
        {
            if (node.tags.length > 0)
            {
                const tag = node.tags[0];
                const payload = 
                {
                    nodeid: created.nodeid, 
                    tagid: tag.tagid
                };
                store.commit(Mutations.addNodeTag, payload);
                SendPacket(PacketType.NodeAddTag, payload);
            }

            if (selectAfterCreation)
            {
                store.commit(Mutations.setSelected, created);
            }
        });
    }

    public clone() : Node
    {
        const clone = new Node(this.nodeid, this.dateStart, this.color);
        clone.type = this.type;
        clone.name = this.name;
        clone.important = this.important;
        clone.description = this.description;
        clone.references = this.references;
        clone.image = this.image;
        clone.imageCaption = this.imageCaption;
        clone.video = this.video;
        clone.audio = this.audio;
        clone.dateEnd = this.dateEnd;
        clone.dateOptions = this.dateOptions;
        clone.tags = this.tags;
        clone.position = this.position;
        clone.relatedArray = this.relatedArray;
        clone.trackID = this.trackID;
        clone.size = this.size;
        clone.component = this.component;
        return clone;
    }
}